import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { HELP_ADULT, HELP_KEYS, HELP_KID } from "@/content/help";
import { lt, t } from "@/i18n";
import { panel } from "@/state/store";

type Tab = "kid" | "adult";

/** How to play: a tab for the child and a tab for teachers and parents. Keyboard: ←→ or 1/2 tabs, ↑↓ scroll, Esc closes. */
export function HelpPanel() {
  const tab = useSignal<Tab>("kid");
  const body = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      const el = body.current;
      switch (e.key) {
        case "ArrowLeft":
        case "ArrowRight":
          e.preventDefault();
          tab.value = tab.value === "kid" ? "adult" : "kid";
          break;
        case "1":
          tab.value = "kid";
          break;
        case "2":
          tab.value = "adult";
          break;
        case "ArrowDown":
          e.preventDefault();
          el?.scrollBy({ top: 60 });
          break;
        case "ArrowUp":
          e.preventDefault();
          el?.scrollBy({ top: -60 });
          break;
        case "PageDown":
        case " ":
          e.preventDefault();
          el?.scrollBy({ top: el.clientHeight * 0.8 });
          break;
        case "PageUp":
          e.preventDefault();
          el?.scrollBy({ top: -el.clientHeight * 0.8 });
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    body.current?.scrollTo({ top: 0 });
  }, [tab.value]);

  const sections = tab.value === "kid" ? HELP_KID : HELP_ADULT;
  return (
    <div class="help">
      <div class="backpack-top">
        <span class="title">❓ {t("help.title")}</span>
        <div class="tabs">
          <button class={tab.value === "kid" ? "on" : ""} onClick={() => (tab.value = "kid")}>
            <kbd>1</kbd> {t("help.tab.kid")}
          </button>
          <button class={tab.value === "adult" ? "on" : ""} onClick={() => (tab.value = "adult")}>
            <kbd>2</kbd> {t("help.tab.adult")}
          </button>
        </div>
        <button class="close" onClick={() => (panel.value = null)}>
          {t("help.close")}
        </button>
      </div>
      <div class={`help-body ${tab.value}`} ref={body}>
        {sections.map((sec) => (
          <section key={lt(sec.title)} class="help-section">
            <h3>
              {sec.icon} {lt(sec.title)}
            </h3>
            <ul>
              {sec.items.map((item) => (
                <li key={lt(item)}>{lt(item)}</li>
              ))}
            </ul>
          </section>
        ))}
        <section class="help-section">
          <h3>⌨️ {t("help.keys")}</h3>
          <table class="help-keys">
            <tbody>
              {HELP_KEYS.map((row) => (
                <tr key={lt(row.action)}>
                  <td>
                    <kbd>{lt(row.keys)}</kbd>
                  </td>
                  <td>{lt(row.action)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      <div class="backpack-foot">{t("help.foot")}</div>
    </div>
  );
}
